import React, { useEffect, useState } from "react";
import type { TableProps } from "antd";
import { Space, Table } from "antd";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { IconPrimary, Popup } from "../../components";
import { CircleTick, CloseIcon } from "../../assets";
import moment from "moment";
import { usePopup } from "../../hooks/usePopup";
import RequestConfirmForm from "../../forms/RequestConfirmForm";
import DenyRequestForm from "../../forms/DenyRequestForm";
import SendQuotationForm from "../../forms/SendQuotation";
import { getCommonState, getRequestState } from "../../redux/store/getState";
import {
  filterRequests,
  getRequestsAtc,
} from "../../redux/apiActions/requestsAction";
import { RequestAtc } from "../../redux/apiSlice/requestSlice";
import modifyId from "../../utils/modifyId";
import useSearch from "../../hooks/useSearch";
import { clearFilterValues } from "../../redux/apiSlice/commonSlice";
import useUser from "../../hooks/useUser";

type ColumnsType<T extends object = object> = TableProps<T>["columns"];

const RequestsTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { requests, loading } = useAppSelector(getRequestState);
  const { filterFrom, filterTo, atc_id } = useAppSelector(getCommonState);
  const [data, setData] = useState<RequestAtc[]>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const popup = usePopup();

  useSearch(requests, setData);

  const { approvePermission } = useUser();

  useEffect(() => {
    if (filterFrom && filterTo && atc_id) {
      dispatch(filterRequests({ from: filterFrom, to: filterTo, atc_id }));
    } else {
      dispatch(getRequestsAtc());
    }
  }, [filterFrom, filterTo, atc_id, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(clearFilterValues());
    };
  }, [dispatch]);

  const columns: ColumnsType<RequestAtc> = [
    {
      title: "Date and Time",
      dataIndex: "created_at",
      render: (created_at) =>
        `${moment(created_at).format("DD/MM/YYYY hh:mmA")}`,
    },
    {
      title: "Request ID",
      dataIndex: "id",
      render: (request_id) => <>{modifyId(request_id)}</>,
    },
    {
      title: "ATC ID",
      dataIndex: "atc",
      render: (atc) => <>{modifyId(atc.id)}</>,
    },
    {
      title: "ATC Name",
      dataIndex: "atc",
      render: (atc) => <>{atc.name}</>,
    },
    {
      title: "Exam Name",
      dataIndex: "exam",
      render: (exam) => <>{exam.name}</>,
    },
    {
      title: "Request",
      dataIndex: "number_of_vouchers",
      render: (voucher, record) => (
        <>
          {voucher}{" "}
          {record.status === "quote_requested" || record.status === "quote_sent"
            ? " Quotation "
            : " Voucher "}
          Requested
        </>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Space size="middle">
          {record.status !== "quote_sent" &&
          record.status !== "completed" &&
          record.status !== "denied" ? (
            <Popup
              openTrigger={
                <IconPrimary disabled={!approvePermission}>
                  <CircleTick />
                </IconPrimary>
              }
              ref={popup.popupRef}
            >
              {record.status === "voucher_requested" ? (
                <RequestConfirmForm record={record} popup={popup} />
              ) : (
                <SendQuotationForm record={record} popup={popup} />
              )}
            </Popup>
          ) : null}
          {record.status !== "quote_requested" &&
          record.status !== "quote_sent" &&
          record.status !== "completed" &&
          record.status !== "denied" ? (
            <Popup
              openTrigger={
                <IconPrimary disabled={!approvePermission}>
                  <CloseIcon />
                </IconPrimary>
              }
              ref={popup.popupRef}
            >
              <DenyRequestForm record={record} popup={popup} />
            </Popup>
          ) : null}

          {record.status === "quote_sent"
            ? "Quotation Sent "
            : record.status === "denied"
            ? "Voucher Denied"
            : record.status === "completed"
            ? "Voucher Sent"
            : ""}
        </Space>
      ),
    },
  ];

  return (
    <Table
      style={{ marginTop: 20, whiteSpace: "pre" }}
      loading={loading.getRequest}
      columns={columns}
      rowKey={(record) => record.id}
      dataSource={data}
      onChange={(p) =>
        setPagination({ current: p.current || 1, pageSize: p.pageSize || 10 })
      }
      pagination={pagination}
      rowClassName={(_, index) => (index % 2 === 0 ? "tr-light" : "tr-dark")}
    />
  );
};

export default RequestsTable;
